/**
 * Cookie-consent storage (GitLab #649).
 *
 * The customer's choice is kept in a first-party, JS-readable cookie
 * (`penny_cookie_consent`). PECR exempts it from needing consent itself,
 * because it records the preference — but that also means it is the one
 * thing that must never be lost, so everything here fails closed: any
 * value that does not parse cleanly is treated as "no choice made", which
 * re-prompts rather than silently assuming permission.
 *
 * It is deliberately NOT `HttpOnly`. The gate is a client component that
 * has to decide before first paint whether to render, and a server
 * round-trip there would produce exactly the flash of content the UX
 * design rules out. The cookie carries no secret — only which categories
 * were agreed to — so JS readability costs nothing.
 *
 * A separate `penny_anonymous_id` cookie holds a random identifier used to
 * attribute a pre-login consent to the customer once they authenticate
 * (D19); the two are written together and cleared together.
 */

import {
  CONSENT_COOKIE_NAME,
  CONSENT_ANONYMOUS_ID_COOKIE_NAME,
  COOKIE_CATEGORIES,
  type AnalyticsSettings,
  type CookieCategory,
} from '@/types/organization';

/** Which categories the customer agreed to. `necessary` is always true. */
export type ConsentCategories = Record<CookieCategory, boolean>;

/** What the customer did, recorded for the server-side evidence log (D15). */
export type ConsentAction =
  | 'granted'
  | 'essential_only'
  | 'updated'
  | 'withdrawn';

/** Shape persisted in the consent cookie. */
export interface StoredConsent {
  /** `consent_version` in force when the choice was made. */
  version: number;
  /** ISO-8601 UTC timestamp of the choice. */
  timestamp: string;
  /** Random identifier linking pre-login consent to the customer (D19). */
  anonymous_id: string;
  categories: ConsentCategories;
}

/** Every optional category refused. */
export function essentialOnlyCategories(): ConsentCategories {
  return {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  };
}

/** Every category granted. */
export function allCategoriesGranted(): ConsentCategories {
  return {
    necessary: true,
    functional: true,
    analytics: true,
    marketing: true,
  };
}

/**
 * Coerce an arbitrary object into a full category map. Anything missing or
 * non-boolean is refused rather than assumed, and `necessary` is forced on
 * because it cannot be declined.
 */
export function normaliseCategories(value: unknown): ConsentCategories {
  const source = (value ?? {}) as Record<string, unknown>;
  const result = essentialOnlyCategories();
  for (const category of COOKIE_CATEGORIES) {
    if (category === 'necessary') continue;
    result[category] = source[category] === true;
  }
  return result;
}

/** Whether any optional category is granted — i.e. whether GTM should load. */
export function hasOptionalConsent(categories: ConsentCategories): boolean {
  return COOKIE_CATEGORIES.some(
    (category) => category !== 'necessary' && categories[category],
  );
}

/**
 * Derive the action to record from a set of categories. `withdrawn` is not
 * inferred here — it depends on the previous state, so the caller passes it.
 */
export function actionForCategories(
  categories: ConsentCategories,
  hadPreviousChoice: boolean,
): ConsentAction {
  if (hadPreviousChoice) return 'updated';
  return hasOptionalConsent(categories) ? 'granted' : 'essential_only';
}

/** Parse the cookie payload. Returns `null` for anything malformed. */
export function parseStoredConsent(raw: string | null): StoredConsent | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

  const candidate = parsed as Record<string, unknown>;
  const version =
    typeof candidate.version === 'number' ? Math.trunc(candidate.version) : NaN;
  const timestamp =
    typeof candidate.timestamp === 'string' ? candidate.timestamp : '';
  const anonymousId =
    typeof candidate.anonymous_id === 'string' ? candidate.anonymous_id : '';

  if (!Number.isFinite(version) || version < 1) return null;
  if (!anonymousId || !isValidAnonymousId(anonymousId)) return null;
  if (!timestamp || Number.isNaN(Date.parse(timestamp))) return null;

  return {
    version,
    timestamp,
    anonymous_id: anonymousId,
    categories: normaliseCategories(candidate.categories),
  };
}

export function serialiseStoredConsent(consent: StoredConsent): string {
  return JSON.stringify(consent);
}

/**
 * Whether a stored choice still stands. A choice expires on the org's
 * configured lifetime (default 180 days) and is invalidated outright when
 * the org bumps `consent_version` — which is how a change to the cookie
 * policy or the GTM tag set re-prompts everybody.
 */
export function isConsentValid(
  consent: StoredConsent | null,
  settings: Pick<AnalyticsSettings, 'consent_version' | 'consent_lifetime_days'>,
  now: Date = new Date(),
): boolean {
  if (!consent) return false;
  if (consent.version !== settings.consent_version) return false;
  const given = Date.parse(consent.timestamp);
  if (Number.isNaN(given)) return false;
  // A timestamp in the future means a tampered cookie or a badly skewed
  // clock; re-prompting is the safe reading of either.
  if (given > now.getTime()) return false;
  const ageDays = (now.getTime() - given) / 86_400_000;
  return ageDays <= settings.consent_lifetime_days;
}

// ---------------------------------------------------------------------------
// Anonymous identifier (D19)
// ---------------------------------------------------------------------------

/**
 * UUID v4 shape. Validated on every read so a tampered cookie cannot smuggle
 * an arbitrary string into the consent-log write path.
 */
const ANONYMOUS_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isValidAnonymousId(value: unknown): value is string {
  return typeof value === 'string' && ANONYMOUS_ID_PATTERN.test(value);
}

/**
 * Generate an anonymous identifier. Uses the Web Crypto API where it exists
 * and falls back to `getRandomValues` for older Safari, which supports the
 * latter but not `randomUUID`. There is no `Math.random` path: a predictable
 * identifier would let one customer's consent row be claimed by another.
 */
export function generateAnonymousId(): string {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef?.randomUUID) return cryptoRef.randomUUID();

  const bytes = new Uint8Array(16);
  cryptoRef.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

// ---------------------------------------------------------------------------
// Browser cookie access
// ---------------------------------------------------------------------------

/** Read a single cookie by name. SSR-safe; returns `null` off the browser. */
export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    if (part.slice(0, eq) !== name) continue;
    try {
      return decodeURIComponent(part.slice(eq + 1));
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * `Secure` is decided from the page protocol rather than `NODE_ENV`, which is
 * inlined at build time and would stamp `Secure` on the plain-HTTP E2E
 * container — where WebKit then silently drops the cookie. Mirrors
 * `browserCookieSecureDefault()` in `lib/auth/secure-cookie-options.ts`.
 */
function cookieSuffix(maxAgeSeconds: number): string {
  const secure =
    typeof window !== 'undefined' && window.location?.protocol === 'https:'
      ? '; Secure'
      : '';
  return `; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}${cookieSuffix(maxAgeSeconds)}`;
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/** Read and validate the stored choice. `null` means "ask again". */
export function readStoredConsent(): StoredConsent | null {
  return parseStoredConsent(readCookie(CONSENT_COOKIE_NAME));
}

/**
 * Return the existing anonymous identifier, minting and persisting one if
 * there is none. Kept for the same lifetime as the consent cookie so a
 * customer who consents, leaves and returns is still recognised as the same
 * anonymous subject when they eventually sign in.
 */
export function readOrMintAnonymousId(lifetimeDays: number): string {
  const existing = readCookie(CONSENT_ANONYMOUS_ID_COOKIE_NAME);
  if (isValidAnonymousId(existing)) return existing;
  const minted = generateAnonymousId();
  writeCookie(
    CONSENT_ANONYMOUS_ID_COOKIE_NAME,
    minted,
    Math.round(lifetimeDays * 86_400),
  );
  return minted;
}

/** Persist a choice for the org's configured lifetime. */
export function writeStoredConsent(
  consent: StoredConsent,
  lifetimeDays: number,
): void {
  writeCookie(
    CONSENT_COOKIE_NAME,
    serialiseStoredConsent(consent),
    Math.round(lifetimeDays * 86_400),
  );
  writeCookie(
    CONSENT_ANONYMOUS_ID_COOKIE_NAME,
    consent.anonymous_id,
    Math.round(lifetimeDays * 86_400),
  );
}

/**
 * Drop the stored choice. The anonymous identifier goes with it — keeping it
 * would let a customer who cleared their consent still be joined to their
 * earlier rows, which is the opposite of what clearing implies.
 */
export function clearStoredConsent(): void {
  deleteCookie(CONSENT_COOKIE_NAME);
  deleteCookie(CONSENT_ANONYMOUS_ID_COOKIE_NAME);
}

// ---------------------------------------------------------------------------
// Withdrawal clean-up (AC10)
// ---------------------------------------------------------------------------

/**
 * Cookies set by the tags GTM delivers. Matched by prefix because the names
 * are per-container or per-site (`_ga_G1234`, `_hjSession_567`).
 *
 * Withdrawal has to actively delete these: refusing consent only stops the
 * tag firing again, it does not remove what a previous acceptance left
 * behind, and a `_ga` cookie surviving a withdrawal is precisely what an
 * ICO audit looks for.
 */
export const OPTIONAL_COOKIE_PREFIXES = [
  '_ga',
  '_gid',
  '_gat',
  '_gcl_au',
  '_hj',
];

/**
 * Session Replay's record of the current recording, written to
 * `sessionStorage` rather than to a cookie (#649, D9).
 *
 * Stopping the recorder clears this itself, but only when the recorder is
 * running. A customer who was recorded, closed the tab and withdrew consent
 * on a later page load would otherwise leave the entry behind, so withdrawal
 * removes it unconditionally.
 */
export const REPLAY_SESSION_STORAGE_KEY = 'sentryReplaySession';

/**
 * Delete every optional cookie we can reach, and the replay session entry.
 *
 * A cookie can only be deleted with the same domain and path it was set
 * with, and GA sets `_ga` on the registrable domain rather than the exact
 * host. We therefore repeat the delete across the current host and each
 * parent domain, and across `/` plus the current path. Third-party cookies
 * on a vendor's own domain are out of reach from here — those stop at the
 * consent signal instead.
 */
export function clearOptionalCookies(): void {
  if (typeof document === 'undefined') return;

  try {
    window.sessionStorage?.removeItem(REPLAY_SESSION_STORAGE_KEY);
  } catch {
    // Storage can throw when it is disabled or full. A withdrawal must not
    // fail because of it; the cookie clean-up below is the part that matters.
  }

  const names = (document.cookie ? document.cookie.split('; ') : [])
    .map((part) => part.slice(0, part.indexOf('=')))
    .filter((name) =>
      OPTIONAL_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix)),
    );
  if (names.length === 0) return;

  const host = typeof window !== 'undefined' ? window.location?.hostname ?? '' : '';
  const labels = host.split('.');
  const domains: Array<string | null> = [null];
  for (let i = 0; i < labels.length - 1; i += 1) {
    domains.push(`.${labels.slice(i).join('.')}`);
  }

  for (const name of names) {
    for (const domain of domains) {
      const domainPart = domain ? `; Domain=${domain}` : '';
      document.cookie = `${name}=; Max-Age=0; Path=/${domainPart}`;
    }
  }
}
